import {
  bucketLast,
  bucketSum,
  getChannelColor,
  getMetricColor,
  MetricUnit,
  SummaryMode,
} from '../charts'
import type {
  BucketGranularity,
  ChartSeriesInput,
  MetricColorRole,
} from '../charts'
import {
  MetricUnit as SourceMetricUnit,
  SummaryMode as SourceSummaryMode,
} from '../MetricChart/types'
import type { MetricSeries } from '../MetricChart/types'

/** Benchmark metric keys keep their semantic reference colours — the channel's
 * own growth-rate line vs a deliberately muted cohort-median line — so they are
 * excluded from the positional palette below. */
const BENCHMARK_COLOR_ROLE: Record<string, MetricColorRole> = {
  'benchmark-growth-rate': 'benchmark-channel',
  'benchmark-median': 'benchmark-median',
}

/** Strip the `-comparison` suffix so a comparison sibling shares its primary's
 * base metric (and therefore its colour). */
const baseKey = (key: string): string => key.replace(/-comparison$/, '')

function benchmarkColorRole(key: string): MetricColorRole | undefined {
  return BENCHMARK_COLOR_ROLE[baseKey(key)]
}

/**
 * Assigns each metric its palette slot by *position* in the chart, mirroring the
 * All-Channels "strict order" model: the 1st series is purple, the 2nd teal, the
 * 3rd orange, … in the order the metrics appear. A single-metric chart therefore
 * gets purple; a two-metric view (e.g. Content Impact) gets purple + teal.
 *
 * Keyed by *base* metric so a `-comparison` sibling inherits its primary's slot,
 * and derived from the distinct bases across all passed series so a
 * comparison-only set (the "Comparison" period view) still resolves the same way.
 * Benchmark keys are omitted — they keep their semantic reference colours.
 */
export function buildMetricPaletteIndex(keys: string[]): Map<string, number> {
  const paletteIndexByKey = new Map<string, number>()
  let nextIndex = 0
  for (const key of keys) {
    const base = baseKey(key)
    if (benchmarkColorRole(base) != null || paletteIndexByKey.has(base))
      continue
    paletteIndexByKey.set(base, nextIndex++)
  }
  return paletteIndexByKey
}

/**
 * The resolved colour token for a metric given the chart's palette-index map:
 * a benchmark metric resolves to its semantic role, everything else to its
 * positional palette slot. Shared by the chart series and the summary swatches
 * so the two can't drift.
 */
export function metricColorToken(
  key: string,
  paletteIndexByKey: Map<string, number>,
): string {
  const role = benchmarkColorRole(key)
  if (role != null) return getMetricColor(role)
  return getChannelColor(paletteIndexByKey.get(baseKey(key)) ?? 0)
}

function toSummaryMode(mode: MetricSeries['summaryMode']): SummaryMode {
  return mode === SourceSummaryMode.Total
    ? SummaryMode.TOTAL
    : SummaryMode.LATEST
}

function toUnit(unit: MetricSeries['unit']): MetricUnit | undefined {
  return unit === SourceMetricUnit.Percentage
    ? MetricUnit.PERCENTAGE
    : undefined
}

/**
 * Maps a narrative view's `MetricSeries` onto the new charts module's
 * `ChartSeriesInput`. Colour follows the strict positional palette (1st series
 * purple, 2nd teal, …), resolved by {@link buildMetricPaletteIndex}; benchmark
 * metrics keep their semantic `colorRole`. The count-per-day (Total)
 * series renders as bars/columns and the cumulative (Latest) series as a line.
 * Dashed metrics become comparison siblings (grouped lighter bars / dashed line).
 *
 * Shared by the column (`PostsColumnChart`/`ColumnChart`) and composed
 * (`ContentImpactChart`/`ComposedChart`) views.
 *
 * On wider ranges the data is bucketed to `granularity` (week/month) so the bars
 * stay readable instead of collapsing to hairlines — Totals are **summed** per
 * bucket, cumulative Latest series take their **last** value. `day` passes the
 * daily points through unchanged.
 */
export function toChartSeriesInputs(
  metrics: MetricSeries[],
  granularity: BucketGranularity = 'day',
): ChartSeriesInput[] {
  const paletteIndexByKey = buildMetricPaletteIndex(metrics.map((m) => m.key))

  return metrics.map((m) => {
    const isTotal = m.summaryMode === SourceSummaryMode.Total
    const data =
      granularity === 'day'
        ? m.data
        : isTotal
          ? bucketSum(m.data, granularity)
          : bucketLast(m.data, granularity)
    // A comparison-period sibling is keyed with a `-comparison` suffix; it renders
    // as a dashed overlay and is dropped from the data table. A `dashed` metric
    // without that suffix (the benchmark cohort median) is a distinct reference
    // line — dashed, but keeping its own colour and its data-table row.
    const isComparison = m.key.endsWith('-comparison')
    const colorRole = benchmarkColorRole(m.key)
    return {
      id: m.key,
      label: m.title,
      icon: m.icon,
      data,
      summaryMode: toSummaryMode(m.summaryMode),
      unit: toUnit(m.unit),
      // Benchmark keys keep their semantic colour; every other metric colours
      // positionally from the shared palette.
      ...(colorRole != null
        ? { colorRole }
        : { paletteIndex: paletteIndexByKey.get(baseKey(m.key)) ?? 0 }),
      kind: isComparison ? 'comparison' : 'primary',
      dashed: Boolean(m.dashed) && !isComparison,
    }
  })
}
